import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { Plus, Trash2, Edit2, Tag, Calendar, Users, CheckCircle, XCircle } from 'lucide-react';
import { formatDateAU } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPromoCodes = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const adminEmail = getAdminEmail();
  
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'free_month',
    discount_value: 1,
    max_uses: 100,
    active: true,
    valid_until: ''
  });

  useEffect(() => {
    if (!adminEmail) {
      navigate('/admin/login');
      return;
    }
    fetchPromoCodes();
  }, [adminEmail, navigate]);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load promo codes",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.description) {
      toast({
        title: "Error",
        description: "Code and description are required",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        created_by: adminEmail,
        discount_value: parseInt(formData.discount_value),
        max_uses: parseInt(formData.max_uses),
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
      };

      await axios.post(`${API}/promo-codes`, payload);
      
      toast({
        title: "Success",
        description: "Promo code created successfully",
      });
      
      setShowCreateForm(false);
      setFormData({
        code: '',
        description: '',
        discount_type: 'free_month',
        discount_value: 1,
        max_uses: 100,
        active: true,
        valid_until: ''
      });
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create promo code",
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const handleDeletePromoCode = async (codeId, code) => {
    setCodeToDelete({ id: codeId, code: code });
    setShowDeleteModal(true);
  };

  const confirmDeletePromoCode = async () => {
    if (!codeToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/promo-codes/${codeToDelete.id}`);
      toast({
        title: "Success",
        description: "Promo code deleted successfully",
      });
      setShowDeleteModal(false);
      setCodeToDelete(null);
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete promo code",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePromoCodeStatus = async (codeId, currentStatus) => {
    try {
      await axios.put(`${API}/promo-codes/${codeId}`, {
        active: !currentStatus
      });
      toast({
        title: "Success",
        description: `Promo code ${!currentStatus ? 'activated' : 'deactivated'}`,
      });
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update promo code status",
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return formatDateAU(dateString);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Promo Codes Management</h1>
            <p className="text-gray-600 mt-2">Create and manage promotional codes for carrier subscriptions</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Promo Code
            </Button>
          </div>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Create New Promo Code</h2>
            <form onSubmit={handleCreatePromoCode} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    placeholder="e.g., FREEMONTH2025"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Will be converted to uppercase</p>
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Input
                  placeholder="e.g., 1 month free trial for new carriers"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount Type</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    data-testid="discount-type-select"
                  >
                    <option value="free_month">Free Month(s) - For Subscriptions</option>
                    <option value="free_quote">Free Quote - For Adhoc Members</option>
                    <option value="percentage">Percentage Discount</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.discount_type === 'free_quote' 
                      ? 'Waives the $49 adhoc fee for one job' 
                      : formData.discount_type === 'percentage'
                      ? 'Percentage off the fee'
                      : 'Number of free trial months'}
                  </p>
                </div>
                <div>
                  <Label>{formData.discount_type === 'percentage' ? 'Discount Percentage' : formData.discount_type === 'free_quote' ? 'Number of Free Quotes' : 'Trial Months'}</Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    min="1"
                    max={formData.discount_type === 'percentage' ? '100' : '12'}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.discount_type === 'percentage' 
                      ? 'Percentage to discount (1-100)' 
                      : formData.discount_type === 'free_quote'
                      ? 'Number of free quotes (usually 1)'
                      : 'Number of free months (default: 1)'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 100 uses</p>
                </div>
                <div>
                  <Label>Valid Until (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="active" className="cursor-pointer">Active immediately</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Create Promo Code
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Promo Codes List */}
        <div className="space-y-4">
          {promoCodes.length === 0 ? (
            <Card className="p-8 text-center">
              <Tag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No promo codes created yet</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                Create Your First Promo Code
              </Button>
            </Card>
          ) : (
            promoCodes.map((promo) => (
              <Card key={promo.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-blue-600">{promo.code}</h3>
                      {promo.active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 mb-4">{promo.description}</p>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          Type
                        </p>
                        <p className="font-semibold text-gray-900">
                          {promo.discount_type === 'free_quote' ? 'Free Quote (Adhoc)' : 
                           promo.discount_type === 'percentage' ? `${promo.discount_value}% Off` :
                           `${promo.discount_value} ${promo.discount_value === 1 ? 'month' : 'months'} free`}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          Usage
                        </p>
                        <p className="font-semibold text-gray-900">
                          {promo.current_uses} / {promo.max_uses || '∞'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Valid Until
                        </p>
                        <p className="font-semibold text-gray-900">{formatDate(promo.valid_until)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Created By</p>
                        <p className="font-semibold text-gray-900 truncate" title={promo.created_by}>
                          {promo.created_by}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => togglePromoCodeStatus(promo.id, promo.active)}
                      title={promo.active ? 'Deactivate' : 'Activate'}
                    >
                      {promo.active ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeletePromoCode(promo.id, promo.code)}
                      className="text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {promo.used_by && promo.used_by.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600 mb-2">Used by:</p>
                    <div className="flex flex-wrap gap-2">
                      {promo.used_by.slice(0, 5).map((email, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {email}
                        </span>
                      ))}
                      {promo.used_by.length > 5 && (
                        <span className="text-xs text-gray-500">
                          +{promo.used_by.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCodeToDelete(null);
        }}
        onConfirm={confirmDeletePromoCode}
        title="Delete Promo Code"
        message={`Are you sure you want to delete promo code "${codeToDelete?.code}"?`}
        confirmText="Delete Promo Code"
        variant="danger"
        loading={loading}
      />

      <Footer />
    </div>
  );
};

export default AdminPromoCodes;
